import AddIcon from "@mui/icons-material/Add";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import WarningIcon from "@mui/icons-material/Warning";
import { UploadResult } from "./SimpleBulkUpload";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import React from "react";
import { useTranslation } from "react-i18next";
import {
  ProgressDialog,
  ProgressState,
} from "../../../components/ProgressDialog";
import { AutocompleteAircraft } from "../../Settings/HangarSettings/AutocompleteAircraft";

interface Trip {
  id: number;
  tail_number: string;
  arrival_date: string;
  departure_date: string;
  aircraft_id?: string;
  make?: string;
  model?: string;
  wingspan?: number;
  length?: number;
}

interface SimpleBulkUploadPresenterProps {
  // Dialog state
  open: boolean;
  onClose: () => void;

  // Data state
  trips: Trip[];
  pasteData: string;
  setPasteData: (data: string) => void;
  loading: boolean;
  isImporting?: boolean;
  error: string | null;
  setError: (error: string | null) => void;

  // Computed values
  validCount: number;
  exampleDates: {
    example1: string;
    example2: string;
    example3: string;
    example4: string;
  };

  // Actions
  handleImportData: () => void;
  downloadSampleFile: () => void;
  addRow: () => void;
  removeRow: (id: number) => void;
  updateTrip: (id: number, field: keyof Trip, value: string) => void;
  updateTripAircraft: (id: number, aircraft: any) => void;
  handleSave: () => void;
  handleClose: () => void;
  activeFBOId: string;

  // Auto-assignment info
  autoAssignLocationName?: string;

  // Render mode
  embedded?: boolean; // If true, renders content without Dialog wrapper

  // Progress tracking
  uploadProgress?: ProgressState;

  // Success state
  uploadResult?: UploadResult | null;
  onViewTrips?: () => void;
}

export const SimpleBulkUploadPresenter: React.FC<
  SimpleBulkUploadPresenterProps
> = ({
  open,
  trips,
  pasteData,
  setPasteData,
  loading,
  isImporting = false,
  error,
  setError,
  validCount,
  exampleDates,
  handleImportData,
  downloadSampleFile,
  addRow,
  removeRow,
  updateTrip,
  updateTripAircraft,
  handleSave,
  handleClose,
  autoAssignLocationName,
  embedded = false,
  uploadProgress,
  uploadResult,
  onViewTrips,
}) => {
  const { t } = useTranslation();

  // Determine if error is actually a success message
  const isSuccessMessage = error?.startsWith("✓");
  const isPartialSuccess = error?.includes("✓") && error?.includes("✗");

  // Helper function to check if dates are valid (arrival < departure)
  const hasInvalidDates = (trip: Trip): boolean => {
    if (!trip.arrival_date || !trip.departure_date) return false;
    return new Date(trip.arrival_date) >= new Date(trip.departure_date);
  };

  // Success state view - shown after successful upload
  const successContent = uploadResult && uploadResult.successCount > 0 && (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        py: 6,
        px: 4,
        textAlign: "center",
      }}
    >
      <CheckCircleOutlineIcon
        sx={{ fontSize: 80, color: "success.main", mb: 2 }}
      />
      <Typography variant="h5" sx={{ mb: 1, fontWeight: 500 }}>
        {t("screens.schedule.simpleBulkUpload.successState.title")}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        {t("screens.schedule.simpleBulkUpload.successState.message", {
          count: uploadResult.successCount,
        })}
        {autoAssignLocationName && (
          <>
            {" "}
            {t("screens.schedule.simpleBulkUpload.successState.assignedTo", {
              locationName: autoAssignLocationName,
            })}
          </>
        )}
      </Typography>
      <Box sx={{ display: "flex", gap: 2 }}>
        {onViewTrips && uploadResult.earliestDate && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<VisibilityIcon />}
            onClick={onViewTrips}
            size="large"
          >
            {t("screens.schedule.simpleBulkUpload.successState.viewTrips")}
          </Button>
        )}
        <Button
          variant={onViewTrips && uploadResult.earliestDate ? "outlined" : "contained"}
          onClick={handleClose}
          size="large"
        >
          {t("screens.schedule.simpleBulkUpload.successState.done")}
        </Button>
      </Box>
    </Box>
  );

  const content = (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      {/* Show location assignment info if auto-assignment is enabled */}
      {autoAssignLocationName && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t("screens.schedule.simpleBulkUpload.messages.autoAssign", { locationName: autoAssignLocationName })}
        </Alert>
      )}

      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          {t("screens.schedule.simpleBulkUpload.pasteExcel.title")}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t("screens.schedule.simpleBulkUpload.pasteExcel.description")}
        </Typography>
        <TextField
          multiline
          rows={4}
          fullWidth
          value={pasteData}
          onChange={(e) => setPasteData(e.target.value)}
          placeholder={`${exampleDates.example1}\n${exampleDates.example2}\n${exampleDates.example3}\n${exampleDates.example4}`}
          disabled={isImporting}
          sx={{ mb: 2 }}
        />
        <Button
          variant="outlined"
          onClick={handleImportData}
          disabled={!pasteData.trim() || isImporting}
          startIcon={isImporting ? <CircularProgress size={16} /> : undefined}
          sx={{ mr: 2 }}
        >
          {isImporting ? t("screens.schedule.simpleBulkUpload.pasteExcel.importing") : t("screens.schedule.simpleBulkUpload.pasteExcel.importButton")}
        </Button>
        <Button variant="text" onClick={downloadSampleFile} sx={{ mr: 2 }}>
          {t("screens.schedule.simpleBulkUpload.pasteExcel.sampleFileButton")}
        </Button>
        {pasteData.trim() && (
          <Typography variant="caption" color="text.secondary">
            {t("screens.schedule.simpleBulkUpload.pasteExcel.rowsReady", { count: pasteData.trim().split("\n").length })}
          </Typography>
        )}
      </Box>

      <Divider sx={{ mb: 2 }} />

      <Typography variant="h6" sx={{ mb: 1 }}>
        {t("screens.schedule.simpleBulkUpload.editTrips.title")}
      </Typography>

      {/* Error/Success display */}
      {error && (
        <Alert
          severity={
            isSuccessMessage && !isPartialSuccess
              ? "success"
              : isPartialSuccess
              ? "warning"
              : "error"
          }
          sx={{ mb: 2 }}
          onClose={() => setError(null)}
        >
          <pre style={{ margin: 0, whiteSpace: "pre-wrap" }}>{error}</pre>
        </Alert>
      )}

      <Table>
        <TableHead>
          <TableRow>
            <TableCell>{t("screens.schedule.simpleBulkUpload.editTrips.tableHeaders.tailNumber")}</TableCell>
            <TableCell>{t("screens.schedule.simpleBulkUpload.editTrips.tableHeaders.aircraft")}</TableCell>
            <TableCell>{t("screens.schedule.simpleBulkUpload.editTrips.tableHeaders.arrivalDate")}</TableCell>
            <TableCell>{t("screens.schedule.simpleBulkUpload.editTrips.tableHeaders.departureDate")}</TableCell>
            <TableCell align="center">
              <Button size="small" onClick={addRow} startIcon={<AddIcon />}>
                {t("screens.schedule.simpleBulkUpload.editTrips.addButton")}
              </Button>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {trips.map((trip) => (
            <TableRow key={trip.id}>
              <TableCell sx={{ py: 2 }}>
                <TextField
                  size="small"
                  value={trip.tail_number}
                  onChange={(e) =>
                    updateTrip(trip.id, "tail_number", e.target.value)
                  }
                  placeholder={t("screens.schedule.simpleBulkUpload.editTrips.placeholders.tailNumber")}
                  sx={{ width: 120 }}
                />
              </TableCell>
              <TableCell sx={{ py: 2 }}>
                {trip.aircraft_id ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <CheckCircleIcon color="success" fontSize="small" />
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {trip.make}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {trip.model}
                      </Typography>
                    </Box>
                  </Box>
                ) : (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <WarningIcon
                      color="warning"
                      fontSize="small"
                      sx={{ flexShrink: 0 }}
                    />
                    <Box sx={{ flexGrow: 1 }}>
                      <AutocompleteAircraft
                        label={t("screens.schedule.simpleBulkUpload.editTrips.selectAircraft")}
                        onSelect={(aircraft) =>
                          updateTripAircraft(trip.id, aircraft)
                        }
                        defaultSelected={null}
                      />
                    </Box>
                  </Box>
                )}
              </TableCell>
              <TableCell sx={{ py: 2 }}>
                <DateTimePicker
                  value={trip.arrival_date ? new Date(trip.arrival_date) : null}
                  onChange={(newValue) =>
                    updateTrip(
                      trip.id,
                      "arrival_date",
                      newValue ? newValue.toISOString().slice(0, 16) : ""
                    )
                  }
                  ampm={false}
                  slotProps={{
                    textField: {
                      size: "small",
                      error: hasInvalidDates(trip),
                      helperText: hasInvalidDates(trip)
                        ? t("screens.schedule.simpleBulkUpload.editTrips.validationErrors.arrivalBeforeDeparture")
                        : "",
                      sx: { width: 200 },
                    },
                  }}
                />
              </TableCell>
              <TableCell sx={{ py: 2 }}>
                <DateTimePicker
                  value={
                    trip.departure_date && trip.departure_date !== "infinity"
                      ? new Date(trip.departure_date)
                      : null
                  }
                  onChange={(newValue) =>
                    updateTrip(
                      trip.id,
                      "departure_date",
                      newValue ? newValue.toISOString().slice(0, 16) : ""
                    )
                  }
                  ampm={false}
                  slotProps={{
                    textField: {
                      size: "small",
                      error: hasInvalidDates(trip),
                      helperText: hasInvalidDates(trip)
                        ? t("screens.schedule.simpleBulkUpload.editTrips.validationErrors.departureAfterArrival")
                        : "",
                      sx: { width: 200 },
                    },
                  }}
                />
              </TableCell>
              <TableCell align="center" sx={{ py: 2 }}>
                <IconButton
                  size="small"
                  onClick={() => removeRow(trip.id)}
                  disabled={trips.length === 1}
                >
                  <DeleteIcon />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </LocalizationProvider>
  );

  // If embedded, just return the content without Dialog wrapper
  if (embedded) {
    return (
      <>
        {successContent || content}
        {/* Progress Dialog for Upload Operation */}
        {loading && uploadProgress && uploadProgress.total > 0 && (
          <ProgressDialog
            open={loading}
            title={t("screens.schedule.simpleBulkUpload.progress.uploadingTitle")}
            message={t("screens.schedule.simpleBulkUpload.progress.uploadingMessage")}
            current={uploadProgress.current}
            total={uploadProgress.total}
            itemName={t("screens.schedule.simpleBulkUpload.progress.itemName")}
            currentItem={uploadProgress.currentItem}
          />
        )}
      </>
    );
  }

  // Otherwise, wrap in Dialog (standalone mode)
  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {successContent
            ? t("screens.schedule.simpleBulkUpload.successState.dialogTitle")
            : t("screens.schedule.simpleBulkUpload.title")}
        </DialogTitle>
        <DialogContent>{successContent || content}</DialogContent>
        {!successContent && (
          <DialogActions>
            <Typography variant="body2" color="text.secondary">
              {t("screens.schedule.simpleBulkUpload.messages.validTripsCount", { count: validCount })}
            </Typography>
            <Button onClick={handleClose} disabled={loading}>
              {t("screens.schedule.simpleBulkUpload.buttons.cancel")}
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={validCount === 0 || loading}
            >
              {loading ? (
                <CircularProgress size={24} />
              ) : (
                t("screens.schedule.simpleBulkUpload.buttons.save", { count: validCount })
              )}
            </Button>
          </DialogActions>
        )}
      </Dialog>

      {/* Progress Dialog for Upload Operation */}
      {loading && uploadProgress && uploadProgress.total > 0 && (
        <ProgressDialog
          open={loading}
          title={t("screens.schedule.simpleBulkUpload.progress.uploadingTitle")}
          message={t("screens.schedule.simpleBulkUpload.progress.uploadingMessage")}
          current={uploadProgress.current}
          total={uploadProgress.total}
          itemName={t("screens.schedule.simpleBulkUpload.progress.itemName")}
          currentItem={uploadProgress.currentItem}
        />
      )}
    </>
  );
};
